// src/services/ErrorTrackingService.js - NEW FILE
// NON-BREAKING error tracking enhancement layer

import * as SentryBrowser from '@sentry/browser';
import * as Sentry from '@sentry/react';

class ErrorTrackingService {
  static isInitialized = false;
  static fallbackMode = false;

  // Initialize Sentry with fallback handling
  static initialize(config = {}) {
    try {
      // Only initialize if not already done
      if (this.isInitialized) return true;

      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV || 'development',
        
        // Conservative sampling for production
        sampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
        
        // Enhanced error filtering
        beforeSend(event, hint) {
          // Don't send errors in development unless explicitly enabled
          if (process.env.NODE_ENV === 'development' && 
              !process.env.REACT_APP_SENTRY_DEBUG) {
            return null;
          }

          // Filter out network errors that aren't actionable
          if (hint?.originalException?.name === 'NetworkError') {
            return null;
          }

          // Log that we're capturing this error (for debugging)
          console.log('📤 Sending error to Sentry:', event.message);
          
          return event;
        },

        // Conservative configuration - remove integrations that might not be available
        integrations: [],

        // Performance monitoring (optional, low impact)
        tracesSampleRate: 0.01,
        
        ...config
      });

      this.isInitialized = true;
      console.log('✅ Sentry initialized successfully');
      return true;

    } catch (error) {
      // CRITICAL: Never let Sentry initialization break the app
      console.warn('⚠️ Sentry initialization failed, using fallback mode:', error);
      this.fallbackMode = true;
      return false;
    }
  }

  // Enhanced error capture that preserves existing console.error calls
  static captureError(error, context = {}, preserveConsoleError = true) {
    try {
      // ALWAYS preserve existing console.error behavior
      if (preserveConsoleError) {
        // This ensures existing console.error calls continue working
        const originalError = console.error;
        if (typeof originalError === 'function') {
          // Don't interfere with existing console output
        }
      }

      // Only send to Sentry if properly initialized
      if (!this.fallbackMode && this.isInitialized) {
        // Enhance context with app-specific data
        const enhancedContext = {
          ...context,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        };

        // Add user context if available (without breaking existing auth)
        if (context.userId && !context.user) {
          SentryBrowser.setUser({ id: context.userId });
        }

        // Set additional context
        if (context.businessId) {
          SentryBrowser.setTag('businessId', context.businessId);
        }

        if (context.component) {
          SentryBrowser.setTag('component', context.component);
        }

        if (context.action) {
          SentryBrowser.setTag('action', context.action);
        }

        // Capture the error
        SentryBrowser.captureException(error, {
          extra: enhancedContext,
          level: context.level || 'error',
        });
      }

      return true;

    } catch (sentryError) {
      // NEVER let Sentry errors break existing functionality
      console.warn('⚠️ Failed to capture error in Sentry:', sentryError);
      return false;
    }
  }

  // Enhanced service error wrapper that preserves existing patterns
  static wrapServiceError(originalError, serviceName, methodName, context = {}) {
    // Capture enhanced error without changing the original error
    this.captureError(originalError, {
      service: serviceName,
      method: methodName,
      component: 'service',
      ...context
    });

    // Return the original error unchanged for existing error handling
    return originalError;
  }

  // Enhanced component error wrapper
  static wrapComponentError(originalError, componentName, context = {}) {
    this.captureError(originalError, {
      component: componentName,
      ...context
    });

    return originalError;
  }

  // Breadcrumb helpers that don't interfere with existing logging
  static addBreadcrumb(message, category = 'custom', level = 'info', data = {}) {
    try {
      if (!this.fallbackMode && this.isInitialized) {
        SentryBrowser.addBreadcrumb({
          message,
          category,
          level,
          data,
          timestamp: Date.now() / 1000,
        });
      }
    } catch (error) {
      // Silent failure to not interfere with app functionality
      console.debug('Failed to add Sentry breadcrumb:', error);
    }
  }

  // Enhanced user context (additive to existing auth)
  static setUserContext(userInfo) {
    try {
      if (!this.fallbackMode && this.isInitialized) {
        SentryBrowser.setUser({
          id: userInfo.id || userInfo.uid,
          email: userInfo.email,
          businessId: userInfo.businessId,
        });
      }
    } catch (error) {
      console.debug('Failed to set Sentry user context:', error);
    }
  }

  // Performance monitoring helpers (optional)
  static startTransaction(name, operation = 'navigation') {
    try {
      if (!this.fallbackMode && this.isInitialized) {
        // Simplified transaction for compatibility
        return {
          finish: () => {}, // No-op for now
          setTag: (key, value) => SentryBrowser.setTag(key, value),
          setData: (key, value) => SentryBrowser.setExtra(key, value)
        };
      }
    } catch (error) {
      console.debug('Failed to start Sentry transaction:', error);
    }
    return null;
  }

  // Health check for monitoring
  static isHealthy() {
    return {
      initialized: this.isInitialized,
      fallbackMode: this.fallbackMode,
      hasConnection: this.isInitialized && !this.fallbackMode,
    };
  }
}

export { ErrorTrackingService };
